import { useEffect, useMemo, useRef, useState } from "react";
import type { CelestialBody, Mission, StarSystem } from "../shared/game-types";

interface Viewport {
  x: number;
  y: number;
  scale: number;
}

const initialViewport: Viewport = { x: 0, y: 0, scale: 1 };

export function StarMap({
  system,
  missions,
  surveyedBodyIds,
  selectedId,
  onSelect,
  showOrbits,
}: {
  system: StarSystem;
  missions: Mission[];
  surveyedBodyIds: Set<string>;
  selectedId?: string;
  onSelect: (body: CelestialBody) => void;
  showOrbits: boolean;
}) {
  const [viewport, setViewport] = useState(initialViewport);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const lastCenter = useRef<{ x: number; y: number } | null>(null);
  const lastDistance = useRef(0);
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const positions = useMemo(() => bodyPositions(system), [system]);

  useEffect(() => setViewport(initialViewport), [system.id]);

  function reset() {
    setViewport(initialViewport);
  }

  function pointerDown(event: React.PointerEvent<SVGSVGElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 1) {
      lastCenter.current = { x: event.clientX, y: event.clientY };
      dragOrigin.current = { x: event.clientX, y: event.clientY };
    } else if (pointers.current.size === 2) {
      const [first, second] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      lastCenter.current = midpoint(first, second);
      lastDistance.current = distance(first, second);
    }
  }

  function pointerMove(event: React.PointerEvent<SVGSVGElement>) {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointers.current.values()];
    if (points.length === 1 && lastCenter.current) {
      const point = points[0]!;
      const dx = point.x - lastCenter.current.x;
      const dy = point.y - lastCenter.current.y;
      if (dragOrigin.current && distance(point, dragOrigin.current) > 4) suppressClick.current = true;
      setViewport((current) => ({ ...current, x: current.x + dx, y: current.y + dy }));
      lastCenter.current = point;
    } else if (points.length >= 2) {
      const first = points[0]!;
      const second = points[1]!;
      const center = midpoint(first, second);
      const nextDistance = distance(first, second);
      const ratio = lastDistance.current ? nextDistance / lastDistance.current : 1;
      suppressClick.current = true;
      setViewport((current) => ({
        x: current.x + (center.x - (lastCenter.current?.x ?? center.x)),
        y: current.y + (center.y - (lastCenter.current?.y ?? center.y)),
        scale: clamp(current.scale * ratio, 0.65, 4),
      }));
      lastCenter.current = center;
      lastDistance.current = nextDistance;
    }
  }

  function pointerUp(event: React.PointerEvent<SVGSVGElement>) {
    pointers.current.delete(event.pointerId);
    const point = [...pointers.current.values()][0];
    lastCenter.current = point ?? null;
    lastDistance.current = 0;
    dragOrigin.current = point ?? null;
    window.setTimeout(() => { suppressClick.current = false; }, 0);
  }

  function keyDown(event: React.KeyboardEvent<SVGSVGElement>) {
    const amount = event.shiftKey ? 60 : 24;
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "+", "=", "-", "Home"].includes(event.key)) event.preventDefault();
    if (event.key === "ArrowLeft") setViewport((current) => ({ ...current, x: current.x + amount }));
    if (event.key === "ArrowRight") setViewport((current) => ({ ...current, x: current.x - amount }));
    if (event.key === "ArrowUp") setViewport((current) => ({ ...current, y: current.y + amount }));
    if (event.key === "ArrowDown") setViewport((current) => ({ ...current, y: current.y - amount }));
    if (event.key === "+" || event.key === "=") setViewport((current) => ({ ...current, scale: clamp(current.scale * 1.2, 0.65, 4) }));
    if (event.key === "-") setViewport((current) => ({ ...current, scale: clamp(current.scale / 1.2, 0.65, 4) }));
    if (event.key === "Home") reset();
  }

  return (
    <div className="map-viewport">
      <svg
        viewBox="0 0 900 640"
        role="group"
        aria-label={`${system.name}交互星图。Tab 聚焦天体，Enter 选择；方向键平移，加减键缩放，Home 适应屏幕。`}
        tabIndex={0}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
        onWheel={(event) => {
          event.preventDefault();
          setViewport((current) => ({ ...current, scale: clamp(current.scale * (event.deltaY > 0 ? 0.9 : 1.1), 0.65, 4) }));
        }}
        onKeyDown={keyDown}
      >
        <title>{system.name}轨道与任务示意图</title>
        <defs>
          <radialGradient id="star-glow"><stop offset="0" stopColor="#fff7d0" /><stop offset=".25" stopColor="#ffd167" /><stop offset="1" stopColor="#ee7d2d" stopOpacity="0" /></radialGradient>
          <filter id="selected-glow"><feGaussianBlur stdDeviation="3" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>
        <g transform={`translate(${450 + viewport.x} ${320 + viewport.y}) scale(${viewport.scale})`}>
          {showOrbits && positions.map(({ body, radius, ry }) => (
            <ellipse key={`orbit-${body.id}`} className="orbit" rx={radius} ry={ry} vectorEffect="non-scaling-stroke" />
          ))}
          {missions.map((mission) => <MissionTrack key={mission.id} mission={mission} positions={positions} />)}
          <circle className="star-halo" r="46" fill="url(#star-glow)" />
          <circle className="star-core" r="15" />
          <text className="map-label star-label" x="0" y="-28" textAnchor="middle">{system.star.name}</text>
          {positions.map(({ body, x, y }) => {
            const selected = selectedId === body.id;
            const surveyed = surveyedBodyIds.has(body.id);
            const size = surveyed ? 6 + Math.min(8, Math.sqrt(Math.max(0.1, body.radiusEarth)) * 2.2) : 8;
            return (
              <g
                key={body.id}
                className={`celestial celestial-${surveyed ? body.type : "unknown"} ${selected ? "selected" : ""}`}
                transform={`translate(${x} ${y})`}
                role="button"
                tabIndex={0}
                aria-label={surveyed ? `${body.name}，环境难度 ${body.environment}级，资源质量 ${body.quality}级，殖民适宜度 ${body.suitability}级` : `${body.name}，尚未完成轨道勘测`}
                aria-pressed={selected}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={() => { if (!suppressClick.current) onSelect(body); }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(body);
                  }
                }}
              >
                <circle className="celestial-hit-target" r="30" />
                {selected && <circle className="selection-ring" r={size + 9} filter="url(#selected-glow)" vectorEffect="non-scaling-stroke" />}
                <circle r={size} />
                <text className="map-label" x="0" y={size + 16} textAnchor="middle">{body.name}</text>
              </g>
            );
          })}
        </g>
      </svg>
      <div className="map-scale" aria-hidden="true">ZOOM {Math.round(viewport.scale * 100)}% · 示意比例</div>
      <div className="map-zoom-controls" aria-label="星图缩放">
        <button className="icon-button" aria-label="放大" onClick={() => setViewport((current) => ({ ...current, scale: clamp(current.scale * 1.2, 0.65, 4) }))}>＋</button>
        <button className="icon-button" aria-label="缩小" onClick={() => setViewport((current) => ({ ...current, scale: clamp(current.scale / 1.2, 0.65, 4) }))}>−</button>
        <button className="button compact" onClick={reset}>适应屏幕</button>
      </div>
    </div>
  );
}

function MissionTrack({ mission, positions }: { mission: Mission; positions: BodyPosition[] }) {
  const origin = positions.find((position) => position.body.id === mission.originBodyId);
  const target = positions.find((position) => position.body.id === mission.targetBodyId);
  if (!origin || !target) return null;
  const route = mission.status === "returning"
    ? `${target.body.name} 至 ${origin.body.name}`
    : `${origin.body.name} 至 ${target.body.name}`;
  return (
    <g className={`mission-track mission-${mission.status}`}>
      <title>{mission.type}任务：{route}</title>
      <line x1={origin.x} y1={origin.y} x2={target.x} y2={target.y} vectorEffect="non-scaling-stroke" />
      <circle cx={(origin.x + target.x) / 2} cy={(origin.y + target.y) / 2} r="3" />
    </g>
  );
}

interface BodyPosition {
  body: CelestialBody;
  radius: number;
  ry: number;
  x: number;
  y: number;
}

function bodyPositions(system: StarSystem): BodyPosition[] {
  const maximum = Math.max(...system.bodies.map((body) => body.orbitAu), 1);
  return system.bodies.map((body, index) => {
    const radius = 58 + Math.sqrt(body.orbitAu / maximum) * 225;
    const ry = radius * Math.max(0.55, 1 - body.eccentricity * 0.55 - Math.abs(body.inclinationDeg) / 180);
    const phase = hash(body.id) / 0xffffffff * Math.PI * 2 + index * 0.4;
    return { body, radius, ry, x: Math.cos(phase) * radius, y: Math.sin(phase) * ry };
  });
}

function hash(value: string) {
  let result = 2166136261;
  for (const character of value) result = Math.imul(result ^ character.charCodeAt(0), 16777619);
  return result >>> 0;
}

function midpoint(a: { x: number; y: number }, b: { x: number; y: number }) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}
